import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import DashboardCard from "../Common/DashboardCard";
import SectionHeader from "../Common/SectionHeader";

// Colors sampled directly from the Figma export, not guessed:
// - segmented control track: #F4F5F7, active pill: #6B4EFF (same purple as
//   the "Retake Quiz" / "Join Now" buttons elsewhere in the dashboard)
// - Pending row card: bg #FFFBEB / border #FEF3C7 (same amber family as the
//   "Pending" StatusChip)
// - "Message admin" link: #6B4EFF, same purple as the active tab
//
// Rejected/Overdue tinting isn't visible in the one Pending-tab screenshot
// we have, so those two reuse the red family already established in
// StatusChip.js (Rejected/Overdue -> #FEE2E2 / #DC2626) for consistency.
// Worth double-checking against Figma once those tabs are visible there.
const TABS = [
    { key: "Pending", label: "Pending", icon: AccessTimeRoundedIcon },
    { key: "Rejected", label: "Rejected", icon: CancelOutlinedIcon },
    { key: "Overdue", label: "Overdue", icon: WarningAmberRoundedIcon },
];

const CARD_TINTS = {
    Pending: { bg: "#FFFBEB", border: "#FEF3C7" },
    Rejected: { bg: "#FEF2F2", border: "#FEE2E2" },
    Overdue: { bg: "#FEF2F2", border: "#FEE2E2" },
};

function ActionItemRow({ title, subtitle, actionLabel = "Message admin", status, onAction }) {
    const tint = CARD_TINTS[status] || CARD_TINTS.Pending;

    return (
        <Box
            sx={{
                bgcolor: "#FFFBEB",
                border: `1px solid ${tint.border}`,
                borderRadius: "8px",
                px: "16px",
                py: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
            }}
        >
            <Box>
                <Typography
                    sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: "#0F172A",
                        lineHeight: "22px",
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontSize: 13,
                        color: "#64748B",
                        mt: 0.25,
                    }}
                >
                    {subtitle}
                </Typography>
            </Box>

            <Box
                onClick={onAction}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    flexShrink: 0,
                    cursor: "pointer",
                    color: "#6B4EFF",
                    fontSize: 14,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                }}
            >
                {actionLabel}
                <ChevronRightIcon sx={{ fontSize: 18 }} />
            </Box>
        </Box>
    );
}

// items: [{ id, title, subtitle, status: 'Pending' | 'Rejected' | 'Overdue', actionLabel }]
export default function ActionItemsCard({ items = [], onAction }) {
    const [activeTab, setActiveTab] = useState("Pending");

    const visibleItems = items.filter((item) => item.status === activeTab);

    return (
        <DashboardCard>
            <SectionHeader title="Action Items" />

            <Box
                sx={{
                    display: "flex",
                    gap: 0.5,
                    bgcolor: "#F4F5F7",
                    borderRadius: "999px",
                    p: "4px",
                    mb: 3,
                }}
            >
                {TABS.map(({ key, label, icon: Icon }) => {
                    const active = activeTab === key;
                    return (
                        <Box
                            key={key}
                            onClick={() => setActiveTab(key)}
                            sx={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                                py: "8px",
                                borderRadius: "999px",
                                cursor: "pointer",
                                bgcolor: active ? "#6B4EFF" : "transparent",
                                color: active ? "#FFFFFF" : "#6B7280",
                                fontSize: 14,
                                fontWeight: 600,
                                transition: "all .15s ease",
                            }}
                        >
                            <Icon sx={{ fontSize: 16 }} />
                            {label}
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {visibleItems.length === 0 ? (
                    <Typography sx={{ fontSize: 14, color: "#94A3B8", textAlign: "center", py: 3 }}>
                        No {activeTab.toLowerCase()} items
                    </Typography>
                ) : (
                    visibleItems.slice(0,3).map((item) => (
                        <ActionItemRow
                            key={item.id}
                            title={item.title}
                            subtitle={item.subtitle}
                            actionLabel={item.actionLabel}
                            status={item.status}
                            onAction={() => onAction?.(item)}
                        />
                    ))
                )}
            </Box>
        </DashboardCard>
    );
}